import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../auth/useAuth';
import { useI18n } from '../../i18n/useI18n';
import {
  getTenantAccessBlocked,
  handlePossibleTenantDeactivatedError,
  subscribeTenantAccessBlocked,
} from '../../query/tenantAccessBlocked';
import { liveEventsService } from '../../services/LiveEventsService';
import {
  applyLiveEvent,
  reloadVisibleLiveEventQueries,
} from './applyLiveEvent';

const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

function wait(delayMs: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }

    const timeoutId = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, delayMs);

    const onAbort = () => {
      window.clearTimeout(timeoutId);
      resolve();
    };

    signal.addEventListener('abort', onAbort, { once: true });
  });
}

export function LiveEventsProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { user, isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const { translate } = useI18n();
  const pathnameRef = useRef(pathname);
  const translateRef = useRef(translate);

  useEffect(() => {
    pathnameRef.current = pathname;
    translateRef.current = translate;
  }, [pathname, translate]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      return;
    }

    if (getTenantAccessBlocked()) {
      return;
    }

    const abortController = new AbortController();
    let reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;

    const unsubscribeBlocked = subscribeTenantAccessBlocked(() => {
      if (getTenantAccessBlocked()) {
        abortController.abort();
      }
    });

    const run = async () => {
      while (!abortController.signal.aborted && !getTenantAccessBlocked()) {
        try {
          await liveEventsService.subscribe(
            {
              onOpen: () => {
                reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
                reloadVisibleLiveEventQueries(queryClient, pathnameRef.current);
              },
              onEvent: (event) => {
                applyLiveEvent(event, {
                  queryClient,
                  pathname: pathnameRef.current,
                  translate: translateRef.current,
                });
              },
            },
            abortController.signal,
          );
        } catch (error) {
          if (abortController.signal.aborted || isAbortError(error)) {
            return;
          }
          if (await handlePossibleTenantDeactivatedError(error)) {
            return;
          }
        }

        if (abortController.signal.aborted || getTenantAccessBlocked()) {
          return;
        }

        await wait(reconnectDelayMs, abortController.signal);
        reconnectDelayMs = Math.min(
          reconnectDelayMs * 2,
          MAX_RECONNECT_DELAY_MS,
        );
      }
    };

    void run();

    return () => {
      unsubscribeBlocked();
      abortController.abort();
    };
  }, [isAuthenticated, queryClient, user?.id]);

  return children;
}
